import { densidadeRelativaAoAr, milhasParaMetros, pesParaMetros } from '../unidades'
import type { Citado, DistanciasGre, IdProduto, Produto } from './tipos'

/**
 * Catálogo de ensino: oito produtos comuns no transporte rodoviário
 * brasileiro, com dados públicos e a fonte de cada número.
 *
 * NÃO é uma base de resposta. O catálogo cobre só o necessário para as
 * oficinas e não substitui a consulta ao GRE/ERG, à FISPQ e ao Manual ABIQUIM.
 */

const c = <T>(valor: T, fonte: Citado['fonte'], observacao?: string): Citado<T> =>
  observacao === undefined ? { valor, fonte } : { valor, fonte, observacao }

const pe = (pes: number) => Math.round(pesParaMetros(pes))
const mi = (milhas: number) => Math.round(milhasParaMetros(milhas))

const ATM_KPA = 101.325
const MMHG_KPA = 0.133322

/* --- Distâncias por guia (ERG 2024, seção "Evacuação" de cada guia) ------- */

const GUIA_115: DistanciasGre = {
  isolamentoInicialM: c(100, 'erg2024', 'Guia 115: isolar pelo menos 100 m em todas as direções'),
  evacuacaoGrandeVazamentoM: c(800, 'erg2024', 'Guia 115: grande vazamento, evacuação inicial a favor do vento'),
  isolamentoTanqueEmFogoM: c(1600, 'erg2024', 'Guia 115: tanque envolvido em fogo, isolar 1.600 m em todas as direções'),
}

const GUIA_LIQUIDO_INFLAMAVEL = (guia: 127 | 128): DistanciasGre => ({
  isolamentoInicialM: c(50, 'erg2024', `Guia ${guia}: isolar pelo menos 50 m em todas as direções`),
  evacuacaoGrandeVazamentoM: c(300, 'erg2024', `Guia ${guia}: grande vazamento, evacuação inicial a favor do vento`),
  isolamentoTanqueEmFogoM: c(800, 'erg2024', `Guia ${guia}: tanque envolvido em fogo, isolar 800 m em todas as direções`),
})

export const PRODUTOS: readonly Produto[] = [
  {
    id: 'glp',
    nome: 'GLP (propano)',
    sinonimos: ['gás liquefeito de petróleo', 'propano', 'gás de cozinha'],
    onu: 1075,
    numeroRisco: c('23', 'adr'),
    classe: c('2.1', 'adr'),
    riscosSubsidiarios: [],
    guiaGre: c(115, 'erg2024'),
    estadoTransporte: 'gasLiquefeitoPressurizado',
    massaMolarGMol: c(44.1, 'niosh', 'propano'),
    densidadeVaporRelativa: c(1.55, 'niosh', 'propano; mais pesado que o ar'),
    densidadeLiquidoKgM3: c(500.1, 'nist', 'propano líquido saturado a 20 °C'),
    pressaoVaporKPa: c(836, 'nist', 'propano saturado a 20 °C (8,36 bar absoluto)'),
    liiPorcento: c(2.1, 'niosh', 'propano'),
    lsiPorcento: c(9.5, 'niosh', 'propano'),
    idlh: { valor: 2100, unidade: 'ppm', fonte: 'niosh', observacao: '10% do LII' },
    aegl60: {
      aegl1: { valor: 5500, unidade: 'ppm', fonte: 'aegl', observacao: 'acima de 10% do LII' },
      aegl2: { valor: 17000, unidade: 'ppm', fonte: 'aegl', observacao: 'acima de 50% do LII' },
      aegl3: { valor: 33000, unidade: 'ppm', fonte: 'aegl', observacao: 'acima do LII: risco de explosão antes do tóxico' },
    },
    calorCombustaoMJKg: c(46.4, 'engineeringToolbox', 'PCI do propano'),
    gre: GUIA_115,
    atencao: [
      'Gás liquefeito sob pressão: tanque exposto ao fogo pode sofrer BLEVE.',
      'Vapor mais pesado que o ar: acumula em baixadas, bueiros e galerias.',
      'Não extinguir chama de vazamento de gás sem conseguir estancar o vazamento.',
    ],
  },
  {
    id: 'amonia',
    nome: 'Amônia anidra',
    sinonimos: ['amoníaco', 'NH₃'],
    onu: 1005,
    numeroRisco: c('268', 'adr'),
    classe: c('2.3', 'adr'),
    riscosSubsidiarios: ['8'],
    guiaGre: c(125, 'erg2024'),
    estadoTransporte: 'gasLiquefeitoPressurizado',
    massaMolarGMol: c(17.0, 'niosh'),
    densidadeVaporRelativa: c(0.6, 'niosh', 'gás mais leve que o ar; o vazamento de líquido forma névoa fria e densa que corre junto ao solo'),
    densidadeLiquidoKgM3: c(611.4, 'nist', 'líquido saturado a 19,3 °C'),
    pressaoVaporKPa: c(839, 'nist', 'saturado a 19,3 °C (8,39 bar absoluto)'),
    liiPorcento: c(15, 'niosh'),
    lsiPorcento: c(28, 'niosh'),
    idlh: { valor: 300, unidade: 'ppm', fonte: 'niosh' },
    aegl60: {
      aegl1: { valor: 30, unidade: 'ppm', fonte: 'aegl' },
      aegl2: { valor: 160, unidade: 'ppm', fonte: 'aegl' },
      aegl3: { valor: 1100, unidade: 'ppm', fonte: 'aegl' },
    },
    calorCombustaoMJKg: null,
    gre: {
      isolamentoInicialM: c(100, 'erg2024', 'Guia 125: isolar pelo menos 100 m em todas as direções'),
      isolamentoTanqueEmFogoM: c(1600, 'erg2024', 'Guia 125: tanque envolvido em fogo, isolar 1.600 m em todas as direções'),
      tabela1Pequeno: {
        isolarM: pe(100),
        protegerDiaM: mi(0.1),
        protegerNoiteM: mi(0.1),
        fonte: 'erg2024',
        observacao: 'Tabela 1, pequeno vazamento: 100 ft; 0,1 mi dia; 0,1 mi noite (convertidos)',
      },
      tabela3CaminhaoTanque: {
        isolarM: pe(500),
        protegerDia: { ventoBaixoM: mi(0.5), ventoModeradoM: mi(0.3), ventoAltoM: mi(0.3) },
        protegerNoite: { ventoBaixoM: mi(1.1), ventoModeradoM: mi(0.4), ventoAltoM: mi(0.4) },
        fonte: 'erg2024',
        observacao: 'Tabela 3, caminhão-tanque/carreta, grande vazamento: 500 ft; dia 0,5/0,3/0,3 mi; noite 1,1/0,4/0,4 mi (vento <6 / 6–12 / >12 mph, convertidos)',
      },
    },
    atencao: [
      'Tóxico por inalação e corrosivo: proteção respiratória autônoma e roupa de proteção química.',
      'Inflamável só em ambiente fechado (LII alto, 15%); ao ar livre o risco dominante é tóxico.',
      'Vazamento de líquido gera nuvem fria e densa, visível, que se comporta como gás pesado no início.',
    ],
  },
  {
    id: 'cloro',
    nome: 'Cloro',
    sinonimos: ['Cl₂'],
    onu: 1017,
    numeroRisco: c('265', 'adr'),
    classe: c('2.3', 'adr'),
    riscosSubsidiarios: ['5.1', '8'],
    guiaGre: c(124, 'erg2024'),
    estadoTransporte: 'gasLiquefeitoPressurizado',
    massaMolarGMol: c(70.9, 'niosh'),
    densidadeVaporRelativa: c(2.47, 'niosh', 'muito mais pesado que o ar'),
    densidadeLiquidoKgM3: c(1413, 'pccCloro', 'líquido a 20 °C'),
    pressaoVaporKPa: c(Math.round(6.8 * ATM_KPA), 'niosh', '6,8 atm'),
    liiPorcento: null,
    lsiPorcento: null,
    idlh: { valor: 10, unidade: 'ppm', fonte: 'niosh' },
    aegl60: {
      aegl1: { valor: 0.5, unidade: 'ppm', fonte: 'aegl' },
      aegl2: { valor: 2.0, unidade: 'ppm', fonte: 'aegl' },
      aegl3: { valor: 20, unidade: 'ppm', fonte: 'aegl' },
    },
    calorCombustaoMJKg: null,
    gre: {
      isolamentoInicialM: c(100, 'erg2024', 'Guia 124: isolar pelo menos 100 m em todas as direções'),
      isolamentoTanqueEmFogoM: c(800, 'erg2024', 'Guia 124: tanque envolvido em fogo, isolar 800 m em todas as direções'),
      tabela1Pequeno: {
        isolarM: pe(200),
        protegerDiaM: mi(0.2),
        protegerNoiteM: mi(0.9),
        fonte: 'erg2024',
        observacao: 'Tabela 1, pequeno vazamento: 200 ft; 0,2 mi dia; 0,9 mi noite (convertidos)',
      },
      tabela3CaminhaoTanque: {
        isolarM: pe(2000),
        protegerDia: { ventoBaixoM: mi(3.5), ventoModeradoM: mi(2.1), ventoAltoM: mi(1.6) },
        protegerNoite: { ventoBaixoM: mi(4.0), ventoModeradoM: mi(2.9), ventoAltoM: mi(2.4) },
        fonte: 'erg2024',
        observacao: 'Tabela 3, caminhão-tanque/carreta, grande vazamento: 2000 ft; dia 3,5/2,1/1,6 mi; noite 4,0/2,9/2,4 mi (vento <6 / 6–12 / >12 mph, convertidos)',
      },
    },
    atencao: [
      'Tóxico por inalação em concentrações muito baixas (IDLH 10 ppm).',
      'Gás denso, amarelo-esverdeado: escoa para baixadas e galerias; o modelo gaussiano subestima o espalhamento junto ao solo.',
      'Não inflamável, mas oxidante: alimenta a combustão de outros materiais.',
    ],
  },
  {
    id: 'gasolina',
    nome: 'Gasolina',
    sinonimos: ['gasolina automotiva', 'gasolina C'],
    onu: 1203,
    numeroRisco: c('33', 'adr'),
    classe: c('3', 'adr'),
    riscosSubsidiarios: [],
    guiaGre: c(128, 'erg2024'),
    estadoTransporte: 'liquido',
    massaMolarGMol: c(110, 'niosh', 'aproximada: mistura de hidrocarbonetos'),
    densidadeVaporRelativa: c(Number(densidadeRelativaAoAr(110).toFixed(1)), 'calculado', 'massa molar aproximada (110 g/mol) ÷ 28,96 g/mol do ar'),
    densidadeLiquidoKgM3: c(740, 'niosh', 'densidade relativa 0,72–0,76; valor médio'),
    pressaoVaporKPa: c(Math.round(300 * MMHG_KPA), 'niosh', 'faixa 38–300 mmHg conforme a formulação; valor do limite superior'),
    liiPorcento: c(1.4, 'niosh'),
    lsiPorcento: c(7.6, 'niosh'),
    idlh: null,
    aegl60: null,
    calorCombustaoMJKg: c(43.4, 'engineeringToolbox', 'PCI'),
    gre: GUIA_LIQUIDO_INFLAMAVEL(128),
    atencao: [
      'Líquido muito inflamável: vapores formam mistura explosiva e se deslocam até fontes de ignição distantes.',
      'Vapor mais pesado que o ar: acumula em bueiros, galerias e porões.',
      'NIOSH classifica como potencial carcinógeno; IDLH não determinado.',
    ],
  },
  {
    id: 'diesel',
    nome: 'Óleo diesel',
    sinonimos: ['diesel', 'óleo diesel B S10'],
    onu: 1202,
    numeroRisco: c('30', 'adr'),
    classe: c('3', 'adr'),
    riscosSubsidiarios: [],
    guiaGre: c(128, 'erg2024'),
    estadoTransporte: 'liquido',
    massaMolarGMol: null,
    densidadeVaporRelativa: null,
    densidadeLiquidoKgM3: null,
    pressaoVaporKPa: null,
    liiPorcento: c(1.0, 'fispqDiesel', 'conferir na FISPQ do fornecedor'),
    lsiPorcento: c(6.0, 'fispqDiesel', 'conferir na FISPQ do fornecedor'),
    idlh: null,
    aegl60: null,
    calorCombustaoMJKg: c(42.6, 'engineeringToolbox', 'PCI'),
    gre: GUIA_LIQUIDO_INFLAMAVEL(128),
    atencao: [
      'Ponto de fulgor acima da temperatura ambiente usual: inflama menos fácil que a gasolina, mas queima intensamente.',
      'Principal impacto costuma ser ambiental: conter antes de atingir córrego e rede pluvial.',
    ],
  },
  {
    id: 'etanol',
    nome: 'Etanol',
    sinonimos: ['álcool etílico', 'álcool combustível'],
    onu: 1170,
    numeroRisco: c('33', 'adr'),
    classe: c('3', 'adr'),
    riscosSubsidiarios: [],
    guiaGre: c(127, 'erg2024'),
    estadoTransporte: 'liquido',
    massaMolarGMol: c(46.1, 'niosh'),
    densidadeVaporRelativa: c(Number(densidadeRelativaAoAr(46.1).toFixed(2)), 'calculado', '46,1 g/mol ÷ 28,96 g/mol do ar'),
    densidadeLiquidoKgM3: c(790, 'niosh', 'densidade relativa 0,79'),
    pressaoVaporKPa: c(Number((44 * MMHG_KPA).toFixed(2)), 'niosh', '44 mmHg'),
    liiPorcento: c(3.3, 'niosh'),
    lsiPorcento: c(19, 'niosh'),
    idlh: { valor: 3300, unidade: 'ppm', fonte: 'niosh', observacao: '10% do LII' },
    aegl60: null,
    calorCombustaoMJKg: c(26.7, 'engineeringToolbox', 'PCI do etanol 100%'),
    gre: GUIA_LIQUIDO_INFLAMAVEL(127),
    atencao: [
      'Chama de álcool é pouco visível à luz do dia.',
      'Miscível em água: usar espuma resistente a álcool (ERG, guia 127).',
    ],
  },
  {
    id: 'acidoSulfurico',
    nome: 'Ácido sulfúrico',
    sinonimos: ['H₂SO₄', 'ácido sulfúrico concentrado'],
    onu: 1830,
    numeroRisco: c('80', 'adr'),
    classe: c('8', 'adr'),
    riscosSubsidiarios: [],
    guiaGre: c(137, 'erg2024'),
    estadoTransporte: 'liquido',
    massaMolarGMol: c(98.1, 'niosh'),
    densidadeVaporRelativa: null,
    densidadeLiquidoKgM3: c(1840, 'niosh', 'densidade relativa 1,84 (ácido 96–98%)'),
    pressaoVaporKPa: c(Number((0.001 * MMHG_KPA).toExponential(2)), 'niosh', '0,001 mmHg: praticamente não evapora'),
    liiPorcento: null,
    lsiPorcento: null,
    idlh: { valor: 15, unidade: 'mg/m3', fonte: 'niosh' },
    aegl60: {
      aegl1: { valor: 0.2, unidade: 'mg/m3', fonte: 'aegl', observacao: 'névoa; valores interinos' },
      aegl2: { valor: 8.7, unidade: 'mg/m3', fonte: 'aegl', observacao: 'névoa; valores interinos' },
      aegl3: { valor: 160, unidade: 'mg/m3', fonte: 'aegl', observacao: 'névoa; valores interinos' },
    },
    calorCombustaoMJKg: null,
    gre: {
      isolamentoInicialM: c(50, 'erg2024', 'Guia 137: isolar pelo menos 50 m (líquidos) em todas as direções'),
      isolamentoTanqueEmFogoM: c(800, 'erg2024', 'Guia 137: tanque envolvido em fogo, isolar 800 m em todas as direções'),
    },
    atencao: [
      'Corrosivo: queimaduras graves por contato; reage violentamente com água (não jogar água no produto).',
      'Não forma pluma tóxica relevante — a oficina de pluma não se aplica; o risco é de contato e de névoa.',
      'Ataca metais liberando hidrogênio inflamável.',
    ],
  },
  {
    id: 'gnv',
    nome: 'GNV (metano comprimido)',
    sinonimos: ['gás natural veicular', 'gás natural comprimido', 'metano'],
    onu: 1971,
    numeroRisco: c('23', 'adr'),
    classe: c('2.1', 'adr'),
    riscosSubsidiarios: [],
    guiaGre: c(115, 'erg2024'),
    estadoTransporte: 'gasComprimido',
    massaMolarGMol: c(16.04, 'nist', 'metano'),
    densidadeVaporRelativa: c(Number(densidadeRelativaAoAr(16.04).toFixed(2)), 'calculado', '16,04 g/mol ÷ 28,96 g/mol do ar; mais leve que o ar'),
    densidadeLiquidoKgM3: null,
    pressaoVaporKPa: null,
    liiPorcento: c(5, 'cameo', 'metano; LII = 50.000 ppm'),
    lsiPorcento: c(15, 'rempec', 'metano'),
    idlh: null,
    aegl60: null,
    calorCombustaoMJKg: c(50.0, 'engineeringToolbox', 'PCI do metano'),
    gre: GUIA_115,
    atencao: [
      'Gás comprimido (não liquefeito): não forma bola de fogo de BLEVE como o GLP, mas o cilindro pode romper no fogo.',
      'Mais leve que o ar: sobe e se dispersa ao ar livre; acumula sob telhados e forros.',
      'Asfixiante simples em ambiente fechado.',
    ],
  },
]

export function produtoPorId(id: IdProduto | string): Produto {
  const achado = PRODUTOS.find((produto) => produto.id === id)
  if (!achado) throw new Error(`Produto desconhecido: ${id}`)
  return achado
}

/** Produtos do catálogo com este número ONU (vazio quando o número não está no catálogo de ensino). */
export function produtosPorOnu(onu: number): Produto[] {
  return PRODUTOS.filter((produto) => produto.onu === onu)
}
